if (!customElements.get('product-form')) {
  // One deadline for the whole add-to-cart submission. Its three requests run
  // sequentially, so per-request timeouts would stack up to triple the wait;
  // a single shared AbortController guarantees the user sees an error within
  // this time of clicking, and a stalled request cannot leave the button
  // spinning forever.
  const ATC_SUBMIT_TIMEOUT_MS = 15000;

  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    appendServiceProperties(formData) {
      const servicesContainer = document.querySelector('.product-services__groups');
      if (!servicesContainer) return;

      const selectedCheckboxes = servicesContainer.querySelectorAll('.product-service-checkbox:checked');
      
      // Collect services for Shopify Functions - cart/checkout
      const servicesForFunctions = [];
      let serviceIndex = 1;

      selectedCheckboxes.forEach(checkbox => {
        const serviceVariantId = checkbox.dataset.variantId;
        const serviceVariantSku = checkbox.dataset.variantSku;
        const serviceTitle = checkbox.dataset.serviceTitle;

        // Indexed properties for Liquid templates
        formData.append(`properties[_service_title_${serviceIndex}]`, serviceTitle);
        formData.append(`properties[_service_sku_${serviceIndex}]`, serviceVariantSku);

        servicesForFunctions.push({
          id: serviceVariantId,
          sku: serviceVariantSku,
          title: serviceTitle
        });
        
        serviceIndex++;
      });

      if (servicesForFunctions.length > 0) {
        formData.append('properties[_services]', JSON.stringify(servicesForFunctions));
      }
    }

    clearSelectedServices() {
      const servicesContainer = document.querySelector('.product-services__groups');
      if (!servicesContainer) return;

      const selectedCheckboxes = servicesContainer.querySelectorAll('.product-service-checkbox:checked');
      selectedCheckboxes.forEach(checkbox => {
        checkbox.checked = false;
      });
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      const startedAt = performance.now();
      let submitSucceeded = false;

      const abortController = new AbortController();
      const submitDeadline = setTimeout(() => abortController.abort(), ATC_SUBMIT_TIMEOUT_MS);

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      this.appendServiceProperties(formData);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData,
        signal: abortController.signal
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`, { signal: abortController.signal });
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`, { signal: abortController.signal });
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        let quantityAdded = 0;
        if (itemInOldCart) {
          const itemInNewCart = newCartData.items.find(
            (item) => item.variant_id === data.variant_id
          );
          quantityAdded = itemInNewCart ? itemInNewCart.quantity - itemInOldCart.quantity : 0;
        } else {
          quantityAdded = data.quantity || 0;
        }

        if (quantityAdded > 0) {
          if (typeof trackCartAction === 'function') {
            trackCartAction('add_to_cart', newCartData, data.variant_id, quantityAdded);
          } else {
            console.error('trackCartAction is not loaded in product-form.js');
          }
        }

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity,
              durationMs: Math.round(performance.now() - startedAt)
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              durationMs: Math.round(performance.now() - startedAt)
            }
          }));
        }

        this.clearSelectedServices();
        submitSucceeded = true;
      } catch (error) {
        console.log(error); // eslint-disable-line
        const isTimeout = error.name === 'AbortError';
        let errorType = 'http';
        if (isTimeout) errorType = 'timeout';
        else if (error instanceof TypeError) errorType = 'network';

        // Server validation messages (e.g. stock limits) are already shown
        // inline by the try block; for timeout/network/other failures show
        // the same native alert the Boost and comparison-page flows use.
        const serverMsgShown = this.errorMsg && !this.errorMsg.hidden;
        if (!serverMsgShown) alert(theme.strings.cartError);

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: serverMsgShown ? this.errorMsg.textContent : theme.strings.cartError,
            errorType,
            errorMessage: error.message,
            durationMs: Math.round(performance.now() - startedAt),
            variantId: formData.get('id')
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        clearTimeout(submitDeadline);
        // Re-enable 'Add to Cart' button.
        if (submitSucceeded) this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
